import React, { useState } from "react";
import {
  X,
  Mail,
  Phone,
  Edit3,
  Save,
  Trash2,
  Loader2,
  XCircle,
} from "lucide-react";
import { EmployeeService } from "../services/employeeService";
import EmployeeDetailsGrid from "./EmployeeDetailsGrid";

export default function EmployeeProfile({
  employee,
  onClose,
  onEmployeeDeleted,
}) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [originalForm, setOriginalForm] = useState(null);
  const [editForm, setEditForm] = useState({
    employee: { ...employee },
    contacts: [],
    addresses: [],
    documents: [],
    orgAssignments: [],
    skills: [],
    certifications: [],
  });

  React.useEffect(() => {
    if (!employee?.uuid) return;
    let isMounted = true;

    const fetchAllDetails = async () => {
      setIsLoadingDetails(true);
      try {
        const [contacts, addresses, docs, orgs, skills] = await Promise.all([
          EmployeeService.getContacts(employee.uuid),
          EmployeeService.getAddresses(employee.uuid),
          EmployeeService.getDocuments(employee.uuid),
          EmployeeService.getOrgAssignments(employee.uuid),
          EmployeeService.getSkillAssignments(employee.uuid),
        ]);

        if (isMounted) {
          const freshData = {
            employee: { ...employee },
            contacts: contacts || [],
            addresses: addresses || [],
            documents: docs || [],
            orgAssignments: orgs || [],
            skills: skills || [],
            certifications: [],
          };
          setEditForm(freshData);
          setOriginalForm(JSON.parse(JSON.stringify(freshData)));
        }
      } catch (error) {
        console.error("Fetch failed", error);
      } finally {
        if (isMounted) setIsLoadingDetails(false);
      }
    };
    fetchAllDetails();

    return () => {
      isMounted = false;
    };
  }, [employee]);

  if (!employee) return null;

  const handleMainChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({
      ...prev,
      employee: { ...prev.employee, [name]: value },
    }));
  };

  const handleNestedChange = (e, arrayName, index) => {
    const { name, value } = e.target;
    const updatedArray = [...editForm[arrayName]];
    updatedArray[index] = { ...updatedArray[index], [name]: value };
    setEditForm((prev) => ({ ...prev, [arrayName]: updatedArray }));
  };

  const handleLanguageChange = (e) => {
    const langsArray = e.target.value
      .split(",")
      .map((lang) => lang.trim())
      .filter(Boolean);
    setEditForm((prev) => ({
      ...prev,
      employee: { ...prev.employee, languages_known: langsArray },
    }));
  };

  const handleAddNested = (arrayName, emptyObj) => {
    setEditForm((prev) => ({
      ...prev,
      [arrayName]: [...prev[arrayName], emptyObj],
    }));
  };

  const handleCancelEdit = () => {
    setEditForm(JSON.parse(JSON.stringify(originalForm)));
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (JSON.stringify(editForm) === JSON.stringify(originalForm)) {
      alert("No changes were detected.");
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await EmployeeService.updateEmployee(employee.uuid, editForm.employee);

      for (const contact of editForm.contacts) {
        if (contact.uuid)
          await EmployeeService.updateContact(
            employee.uuid,
            contact.uuid,
            contact,
          );
        else if (contact.full_name)
          await EmployeeService.createContact(employee.uuid, contact);
      }
      for (const address of editForm.addresses) {
        if (address.uuid)
          await EmployeeService.updateAddress(
            employee.uuid,
            address.uuid,
            address,
          );
        else if (address.line1)
          await EmployeeService.createAddress(employee.uuid, address);
      }
      for (const org of editForm.orgAssignments) {
        if (org.uuid)
          await EmployeeService.updateOrgAssignment(
            employee.uuid,
            org.uuid,
            org,
          );
        else if (org.department)
          await EmployeeService.createOrgAssignment(employee.uuid, org);
      }
      for (const skill of editForm.skills) {
        if (skill.uuid)
          await EmployeeService.updateSkillAssignment(
            employee.uuid,
            skill.uuid,
            skill,
          );
        else if (skill.name)
          await EmployeeService.createSkillAssignment(employee.uuid, skill);
      }

      setOriginalForm(JSON.parse(JSON.stringify(editForm)));
      alert("Employee data successfully updated in database!");
      setIsEditing(false);
    } catch (error) {
      alert("Error saving updates. Check console for details.");
    } finally {
      setIsSaving(false);
    }
  };

  // Direct Permanent Delete Handler hitting the database API
  const handleDirectDelete = async () => {
    const confirmDelete = window.confirm(
      `Are you sure you want to PERMANENTLY delete ${employee.full_name} from the database? This action cannot be undone.`,
    );
    if (!confirmDelete) return;

    setIsDeleting(true);
    try {
      // Direct API call to delete record permanently from backend database
      await EmployeeService.deleteEmployee(employee.uuid);

      alert(
        `${employee.full_name} has been permanently deleted from the database.`,
      );
      if (onEmployeeDeleted) onEmployeeDeleted(employee.uuid);
      onClose();
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Failed to delete employee from the database.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/40 backdrop-blur-sm flex justify-end transition-opacity">
      <div className="bg-slate-50/95 w-full lg:w-1/2 max-w-4xl h-full overflow-y-auto shadow-2xl p-4 md:p-6 flex flex-col relative animate-in slide-in-from-right duration-300 border-l border-white/50">
        {/* TOP BAR */}
        <div className="flex items-center justify-between mb-5 bg-white/60 p-2.5 px-4 rounded-xl border border-slate-200/60 shadow-sm backdrop-blur-xl">
          <div className="text-[11px] text-slate-500 flex items-center gap-2">
            <span>Directory</span>
            <span>/</span>
            <span className="font-bold text-slate-800 tracking-tight">
              Employee Profile
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                  className="flex items-center gap-1.5 bg-white hover:bg-slate-100 border border-slate-200 text-slate-600 px-3 py-1.5 rounded-lg text-[11px] font-semibold shadow-sm transition-all disabled:opacity-50"
                >
                  <XCircle className="w-3.5 h-3.5" /> Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-1.5 rounded-lg text-[11px] font-semibold shadow-md transition-all disabled:opacity-50"
                >
                  {isSaving ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Save className="w-3.5 h-3.5" />
                  )}
                  {isSaving ? "Saving..." : "Save Changes"}
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-1.5 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 px-4 py-1.5 rounded-lg text-[11px] font-semibold shadow-sm transition-all cursor-pointer"
              >
                <Edit3 className="w-3.5 h-3.5 text-blue-600" /> Edit Mode
              </button>
            )}

            {/* Direct Permanent Delete Button */}
            <button
              onClick={handleDirectDelete}
              disabled={isDeleting}
              className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white px-3.5 py-1.5 rounded-lg text-[11px] font-semibold shadow-sm transition-all disabled:opacity-50 cursor-pointer"
            >
              {isDeleting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Trash2 className="w-3.5 h-3.5" />
              )}
              {isDeleting ? "Deleting..." : "Delete Employee"}
            </button>

            <button
              onClick={onClose}
              className="p-1.5 bg-white border border-slate-200 hover:bg-slate-100 rounded-lg text-slate-500 transition-all shadow-sm ml-1 cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* PROFILE OVERVIEW CARD */}
        <div
          className={`bg-white rounded-2xl border ${isEditing ? "border-blue-300 shadow-[0_0_15px_rgba(59,130,246,0.1)]" : "border-slate-200/80 shadow-sm"} p-5 mb-5 flex flex-col xl:flex-row gap-5 items-start xl:items-center justify-between transition-all duration-300 relative`}
        >
          <div className="flex items-center gap-4">
            <div className="relative shrink-0">
              {editForm.employee.avatar ? (
                <img
                  src={editForm.employee.avatar}
                  alt="Profile"
                  className="w-20 h-20 rounded-xl object-cover shadow-sm border border-slate-200"
                />
              ) : (
                <div className="flex items-center justify-center w-20 h-20 rounded-xl bg-gradient-to-tr from-blue-50 to-slate-100 text-blue-600 font-bold text-2xl shrink-0 border border-slate-200 shadow-sm">
                  {editForm.employee.full_name
                    ? editForm.employee.full_name.charAt(0).toUpperCase()
                    : "U"}
                </div>
              )}
              <span
                className={`absolute -bottom-1 -right-1 w-4 h-4 border-2 border-white rounded-full ${editForm.employee.status === "active" ? "bg-emerald-500" : "bg-amber-400"}`}
              ></span>
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2">
                {isEditing ? (
                  <input
                    type="text"
                    name="full_name"
                    value={editForm.employee.full_name || ""}
                    onChange={handleMainChange}
                    className="border border-slate-200 rounded-md px-2 py-1 text-lg font-bold text-slate-900 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                  />
                ) : (
                  <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                    {editForm.employee.full_name || "N/A"}
                  </h2>
                )}
                {!isEditing && (
                  <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider">
                    {editForm.employee.status || "N/A"}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-[11px] font-mono text-slate-500">
                Code:{" "}
                {isEditing ? (
                  <input
                    type="text"
                    name="employee_code"
                    value={editForm.employee.employee_code || ""}
                    onChange={handleMainChange}
                    className="border border-slate-200 rounded px-1.5 py-0.5 bg-slate-50 focus:bg-white focus:outline-none w-20"
                  />
                ) : (
                  <span className="text-slate-700 font-semibold">
                    {editForm.employee.employee_code || "N/A"}
                  </span>
                )}
              </div>
              <div>
                {isEditing ? (
                  <input
                    type="text"
                    name="remarks"
                    value={editForm.employee.remarks || ""}
                    onChange={handleMainChange}
                    placeholder="Role / Remarks"
                    className="border border-slate-200 rounded px-1.5 py-1 text-[11px] text-blue-600 font-semibold bg-blue-50/30 focus:bg-white focus:outline-none w-full mt-1"
                  />
                ) : (
                  <p className="text-xs font-semibold text-blue-600 tracking-tight">
                    {editForm.employee.remarks || "No Role Assigned"}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t xl:border-t-0 xl:border-l border-slate-100 xl:pl-6 pt-4 xl:pt-0 w-full xl:w-auto">
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-full bg-slate-50 flex items-center justify-center shrink-0">
                <Mail className="w-3.5 h-3.5 text-slate-400" />
              </div>
              <div className="flex flex-col w-full">
                <span className="text-[9px] text-slate-400 font-medium uppercase tracking-wider">
                  Email Address
                </span>
                {isEditing ? (
                  <input
                    type="email"
                    name="official_email"
                    value={editForm.employee.official_email || ""}
                    onChange={handleMainChange}
                    className="border border-slate-200 rounded px-1.5 py-0.5 text-[11px] bg-slate-50 focus:bg-white focus:outline-none w-full mt-0.5"
                  />
                ) : (
                  <span className="font-semibold text-slate-800 text-xs">
                    {editForm.employee.official_email || "N/A"}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-full bg-slate-50 flex items-center justify-center shrink-0">
                <Phone className="w-3.5 h-3.5 text-slate-400" />
              </div>
              <div className="flex flex-col">
                <span className="text-[9px] text-slate-400 font-medium uppercase tracking-wider">
                  Phone Number
                </span>
                {isEditing ? (
                  <input
                    type="text"
                    name="primary_phone"
                    value={editForm.employee.primary_phone || ""}
                    onChange={handleMainChange}
                    className="border border-slate-200 rounded px-1.5 py-0.5 text-[11px] bg-slate-50 focus:bg-white focus:outline-none font-mono mt-0.5"
                  />
                ) : (
                  <span className="font-semibold text-slate-800 text-xs font-mono">
                    {editForm.employee.primary_phone || "N/A"}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ACCORDION GRID COMPONENT */}
        <div className="bg-white rounded-2xl border border-slate-200/80 p-5 md:p-6 shadow-sm flex-1 relative">
          {isLoadingDetails && (
            <div className="absolute inset-0 bg-white/60 backdrop-blur-sm z-10 rounded-2xl flex items-center justify-center">
              <div className="flex items-center gap-3 text-blue-600 bg-white px-5 py-2.5 rounded-xl shadow-lg border border-slate-100">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-xs font-semibold tracking-tight">
                  Syncing Records...
                </span>
              </div>
            </div>
          )}

          <EmployeeDetailsGrid
            editForm={editForm}
            isEditing={isEditing}
            onChangeMain={handleMainChange}
            onChangeNested={handleNestedChange}
            onChangeLanguage={handleLanguageChange}
            onAddNested={handleAddNested}
          />
        </div>
      </div>
    </div>
  );
}
